import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faBookOpen,
  faUserGraduate,
  faClipboardList,
  faEnvelope,
  faArrowRight,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface CourseSummary {
  learnershipId: string;
  title: string;
  moduleCount: number;
  enrolledStudentCount: number;
  pendingSubmissionsToGrade: number;
}

interface UpcomingAssignment {
  assignmentId: string;
  learnershipTitle: string;
  title: string;
  dueDate: string;
  submittedCount: number;
  totalStudents: number;
}

interface RecentSubmission {
  submissionId: string;
  assignmentId: string;
  assignmentTitle: string;
  studentName: string;
  submittedAt: string | null;
  status: number;
}

interface DashboardData {
  totalCourses: number;
  totalStudents: number;
  pendingAssignmentsToGrade: number;
  unreadMessages: number;
  courses: CourseSummary[];
  upcomingDueAssignments: UpcomingAssignment[];
  recentSubmissions: RecentSubmission[];
}

export default function InstructorDashboardPage() {
  const { user, accessToken } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch('/api/instructor/dashboard', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error('Failed to load dashboard');
        setData(await response.json());
      } catch (err) {
        console.error(err);
        setError('Failed to load your dashboard. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboard();
  }, [accessToken]);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Welcome back, {user?.firstName}!</h1>
        <p className="text-sm text-brand-900/60 mt-1">Here's what's happening across your courses today.</p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && data && (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[
                { label: 'Assigned Courses', value: data.totalCourses, icon: faBookOpen },
                { label: 'Total Students', value: data.totalStudents, icon: faUserGraduate },
                { label: 'Pending Grading', value: data.pendingAssignmentsToGrade, icon: faClipboardList },
                { label: 'Unread Messages', value: data.unreadMessages, icon: faEnvelope },
              ].map((stat) => (
                <div key={stat.label} className="card-surface p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-brand-900/60">{stat.label}</p>
                      <p className="text-3xl font-bold text-brand-950 mt-2">{stat.value}</p>
                    </div>
                    <FontAwesomeIcon icon={stat.icon} className="h-8 w-8 text-brand-600" />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Courses */}
              <div className="card-surface p-6">
                <h2 className="text-lg font-bold text-brand-950 mb-4">Your Courses</h2>
                {data.courses.length === 0 ? (
                  <p className="text-sm text-brand-900/60">
                    You haven't been assigned to any courses yet. Contact an administrator to get set up.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {data.courses.map((course) => (
                      <Link
                        key={course.learnershipId}
                        to={`/instructor/courses/${course.learnershipId}`}
                        className="block border border-canvas-300 rounded-2xl p-4 hover:border-brand-300 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-brand-950">{course.title}</h3>
                          {course.pendingSubmissionsToGrade > 0 && (
                            <span className="px-2.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full whitespace-nowrap">
                              {course.pendingSubmissionsToGrade} to grade
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-brand-900/60">
                          {course.enrolledStudentCount} students · {course.moduleCount} modules
                        </p>
                      </Link>
                    ))}
                  </div>
                )}
                <Link
                  to="/instructor/courses"
                  className="w-full mt-6 text-center py-2 text-brand-700 hover:text-brand-800 font-semibold text-sm flex items-center justify-center gap-1"
                >
                  View all <FontAwesomeIcon icon={faArrowRight} className="h-3.5 w-3.5" />
                </Link>
              </div>

              {/* Upcoming deadlines */}
              <div className="card-surface p-6">
                <h2 className="text-lg font-bold text-brand-950 mb-4">Upcoming Due Dates</h2>
                {data.upcomingDueAssignments.length === 0 ? (
                  <p className="text-sm text-brand-900/60">No upcoming assignment deadlines.</p>
                ) : (
                  <div className="space-y-4">
                    {data.upcomingDueAssignments.map((a) => (
                      <div key={a.assignmentId} className="flex items-start justify-between border border-canvas-300 rounded-2xl p-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-brand-950 truncate">{a.title}</h3>
                          <p className="text-sm text-brand-900/60 mt-1 truncate">{a.learnershipTitle}</p>
                          <p className="text-xs text-brand-900/50 mt-1">
                            {a.submittedCount}/{a.totalStudents} submitted
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-canvas-200 text-brand-700 text-xs font-semibold rounded-full flex items-center gap-1 whitespace-nowrap">
                          <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                          {formatDate(a.dueDate)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent submissions */}
            <div className="mt-8 card-surface p-6">
              <h2 className="text-lg font-bold text-brand-950 mb-4">Recent Student Activity</h2>
              {data.recentSubmissions.length === 0 ? (
                <p className="text-sm text-brand-900/60">No recent submissions.</p>
              ) : (
                <div className="divide-y divide-canvas-200">
                  {data.recentSubmissions.map((s) => (
                    <div key={s.submissionId} className="py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-950 truncate">{s.studentName}</p>
                        <p className="text-xs text-brand-900/60 truncate">submitted "{s.assignmentTitle}"</p>
                      </div>
                      <span className="text-xs text-brand-900/50 whitespace-nowrap">
                        {s.submittedAt ? formatDate(s.submittedAt) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
